import { useEffect, useRef, useState } from "react";
import { fetchOhlc } from "../api/client";
import type { OhlcCandle } from "../types";
import { parseDbTimestamp } from "../lib/format";

export interface UseOhlcOptions {
  granularity: string; // e.g. "1 minute"
  /** Number of bars to load on initial / timeframe-change fetch. */
  initialBars: number;
  /** Number of trailing bars to fetch on each poll update. */
  updateBars: number;
  /** Poll interval for incremental updates. */
  intervalMs?: number;
}

export interface UseOhlcResult {
  data: OhlcCandle[] | null;
  error: Error | null;
  loading: boolean;
  lastUpdatedAt: number | null;
}

/**
 * OHLC loader that fetches a large initial window once, then polls only the
 * trailing bars. New bars overwrite existing bars with the same `start`, so
 * the latest in-progress candle keeps refreshing. The series is capped at
 * `initialBars` entries to bound memory.
 */
export function useOhlc(
  symbol: string,
  { granularity, initialBars, updateBars, intervalMs = 5_000 }: UseOhlcOptions
): UseOhlcResult {
  const [data, setData] = useState<OhlcCandle[] | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);

  // Keep latest series in a ref so poll updates don't retrigger the effect.
  const dataRef = useRef<OhlcCandle[] | null>(null);
  dataRef.current = data;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const barMs = parseGranMs(granularity);

    // Reset on symbol/granularity change.
    setData(null);
    dataRef.current = null;
    setLoading(true);
    setError(null);

    const loadInitial = async () => {
      controller = new AbortController();
      try {
        const end = Date.now();
        const start = new Date(end - initialBars * barMs);
        const resp = await fetchOhlc(
          symbol,
          start,
          granularity,
          undefined,
          controller.signal
        );
        if (cancelled) return;
        const candles = sortAndDedupe(resp.candles).slice(-initialBars);
        setData(candles);
        dataRef.current = candles;
        setLastUpdatedAt(Date.now());
        setLoading(false);
        scheduleNext();
      } catch (err) {
        if (cancelled) return;
        if ((err as { name?: string }).name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setLoading(false);
        // Retry after the normal interval even on failure.
        scheduleNext();
      }
    };

    const tickUpdate = async () => {
      controller = new AbortController();
      try {
        const start = new Date(Date.now() - updateBars * barMs);
        const resp = await fetchOhlc(
          symbol,
          start,
          granularity,
          undefined,
          controller.signal
        );
        if (cancelled) return;
        const merged = mergeCandles(
          dataRef.current ?? [],
          resp.candles,
          initialBars
        );
        setData(merged);
        dataRef.current = merged;
        setLastUpdatedAt(Date.now());
        setError(null);
      } catch (err) {
        if (cancelled) return;
        if ((err as { name?: string }).name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelled) scheduleNext();
      }
    };

    const scheduleNext = () => {
      timer = setTimeout(tickUpdate, intervalMs);
    };

    loadInitial();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [symbol, granularity, initialBars, updateBars, intervalMs]);

  return { data, error, loading, lastUpdatedAt };
}

function parseGranMs(gran: string): number {
  const m = /^\s*(\d+)\s*(second|minute|hour|day)s?\s*$/i.exec(gran);
  if (!m) return 60_000;
  const n = parseInt(m[1], 10);
  const unit = m[2].toLowerCase();
  const mul =
    unit === "day"
      ? 86_400_000
      : unit === "hour"
      ? 3_600_000
      : unit === "minute"
      ? 60_000
      : 1_000;
  return n * mul;
}

function sortAndDedupe(candles: OhlcCandle[]): OhlcCandle[] {
  const map = new Map<string, OhlcCandle>();
  for (const c of candles) map.set(c.start, c);
  return Array.from(map.values()).sort(
    (a, b) => parseDbTimestamp(a.start) - parseDbTimestamp(b.start)
  );
}

function mergeCandles(
  existing: OhlcCandle[],
  fresh: OhlcCandle[],
  cap: number
): OhlcCandle[] {
  const map = new Map<string, OhlcCandle>();
  for (const c of existing) map.set(c.start, c);
  for (const c of fresh) map.set(c.start, c); // fresh overwrites existing
  const merged = Array.from(map.values()).sort(
    (a, b) => parseDbTimestamp(a.start) - parseDbTimestamp(b.start)
  );
  return merged.slice(-cap);
}
